import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { createBlogPost, updateBlogPost, getBlogPost } from '../../services/firebase/blogService';
import './BlogPostForm.css';

const BlogPostForm = ({ postId = null, onSave = null }) => {
  const navigate = useNavigate();
  const { user, userData } = useAuth();

  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: 'art',
    featuredImage: '',
    tags: [],
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const categories = [
    { id: 'art', label: '🎨 Art & Design' },
    { id: 'tutorial', label: '📚 Tutorials' },
    { id: 'trends', label: '📈 Trends' },
    { id: 'community', label: '👥 Community' },
    { id: 'news', label: '📰 News' },
    { id: 'tips', label: '💡 Tips & Tricks' },
  ];

  // Load post if editing
  useEffect(() => {
    if (postId) {
      loadPost();
    }
  }, [postId]);

  const loadPost = async () => {
    try {
      setLoading(true);
      const result = await getBlogPost(postId);
      
      if (result.success) {
        const post = result.data;
        setFormData({
          title: post.title,
          excerpt: post.excerpt,
          content: post.content,
          category: post.category,
          featuredImage: post.featuredImage,
          tags: post.tags || [],
        });
        setIsEditing(true);
      } else {
        setError(result.error || 'Failed to load post');
      }
    } catch (err) {
      setError('Error loading post: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    setError('');
  };

  const handleAddTag = (e) => {
    e.preventDefault();
    const trimmedTag = tagInput.trim();
    
    if (trimmedTag && !formData.tags.includes(trimmedTag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, trimmedTag],
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove),
    }));
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setError('Title is required');
      return false;
    }
    if (formData.title.trim().length < 5) {
      setError('Title must be at least 5 characters');
      return false;
    }
    if (!formData.excerpt.trim()) {
      setError('Excerpt is required');
      return false;
    }
    if (formData.excerpt.trim().length < 20) {
      setError('Excerpt must be at least 20 characters');
      return false;
    }
    if (!formData.content.trim()) {
      setError('Content is required');
      return false;
    }
    if (formData.content.trim().length < 100) {
      setError('Content must be at least 100 characters');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const postData = {
        title: formData.title.trim(),
        excerpt: formData.excerpt.trim(),
        content: formData.content.trim(),
        category: formData.category,
        featuredImage: formData.featuredImage.trim(),
        tags: formData.tags,
        vendorId: user.uid,
        vendorName: userData?.displayName || user.email,
        vendorAvatar: userData?.profilePicture || '',
      };

      let result;
      if (isEditing) {
        result = await updateBlogPost(postId, postData);
        if (result.success) {
          setSuccess('✓ Post updated successfully! Pending admin review.');
          setTimeout(() => {
            if (onSave) onSave();
            navigate('/vendor/blog-management');
          }, 1500);
        }
      } else {
        result = await createBlogPost(postData);
        if (result.success) {
          setSuccess('✓ Post created successfully! It\'s pending admin review.');
          setTimeout(() => {
            if (onSave) onSave();
            navigate('/vendor/blog-management');
          }, 1500);
        }
      }

      if (!result.success) {
        setError(result.error || 'Failed to save post');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="blog-form-container">
        <div className="blog-form-error">
          Please log in to create a blog post
        </div>
      </div>
    );
  }

  return (
    <div className="blog-form-container">
      <div className="blog-form-card">
        <h1 className="blog-form-title">
          {isEditing ? '✏️ Edit Blog Post' : '✍️ Create Blog Post'}
        </h1>

        {error && (
          <div className="blog-form-alert alert-error">
            ❌ {error}
          </div>
        )}

        {success && (
          <div className="blog-form-alert alert-success">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="blog-form">
          {/* Title */}
          <div className="form-group">
            <label htmlFor="title">Post Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Enter an engaging title..."
              maxLength={100}
              disabled={loading}
              required
            />
            <small>{formData.title.length}/100 characters</small>
          </div>

          {/* Excerpt */}
          <div className="form-group">
            <label htmlFor="excerpt">Excerpt (Summary) *</label>
            <textarea
              id="excerpt"
              name="excerpt"
              value={formData.excerpt}
              onChange={handleInputChange}
              placeholder="Brief summary of your post (shown in listings)..."
              rows="3"
              maxLength={200}
              disabled={loading}
              required
            />
            <small>{formData.excerpt.length}/200 characters</small>
          </div>

          {/* Featured Image */}
          <div className="form-group">
            <label htmlFor="featuredImage">Featured Image URL</label>
            <input
              type="url"
              id="featuredImage"
              name="featuredImage"
              value={formData.featuredImage}
              onChange={handleInputChange}
              placeholder="https://example.com/image.jpg"
              disabled={loading}
            />
            {formData.featuredImage && (
              <div className="image-preview">
                <img src={formData.featuredImage} alt="Featured" />
              </div>
            )}
          </div>

          {/* Category */}
          <div className="form-group">
            <label htmlFor="category">Category *</label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              disabled={loading}
              required
            >
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          {/* Tags */}
          <div className="form-group">
            <label htmlFor="tagInput">Tags</label>
            <div className="tag-input-wrapper">
              <input
                type="text"
                id="tagInput"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                placeholder="Add tags (e.g., watercolor, digital-art) and press Add"
                disabled={loading}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag(e);
                  }
                }}
              />
              <button
                type="button"
                className="btn-add-tag"
                onClick={handleAddTag}
                disabled={loading || !tagInput.trim()}
              >
                Add Tag
              </button>
            </div>
            {formData.tags.length > 0 && (
              <div className="tags-list">
                {formData.tags.map(tag => (
                  <span key={tag} className="tag-chip">
                    {tag}
                    <button
                      type="button"
                      className="tag-remove"
                      onClick={() => handleRemoveTag(tag)}
                      disabled={loading}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="form-group full-width">
            <label htmlFor="content">Post Content *</label>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleInputChange}
              placeholder="Write your blog post content here..."
              rows="12"
              disabled={loading}
              required
            />
            <small>{formData.content.length} characters | Minimum 100 required</small>
          </div>

          {/* Preview */}
          <div className="form-group full-width">
            <label>Preview</label>
            <div className="preview-box">
              <h3>{formData.title || 'Your title here...'}</h3>
              <p className="preview-excerpt">{formData.excerpt || 'Your excerpt here...'}</p>
              <p className="preview-content">
                {formData.content.substring(0, 200)}
                {formData.content.length > 200 ? '...' : ''}
              </p>
            </div>
          </div>

          {/* Buttons */}
          <div className="form-actions">
            <button
              type="submit"
              className="btn-submit"
              disabled={loading}
            >
              {loading ? '⏳ Saving...' : (isEditing ? '💾 Update Post' : '📤 Create Post')}
            </button>
            <button
              type="button"
              className="btn-cancel"
              onClick={() => navigate('/vendor/blog-management')}
              disabled={loading}
            >
              Cancel
            </button>
          </div>

          {/* Info Message */}
          <div className="form-info">
            <p>
              📝 <strong>Note:</strong> Your post will be reviewed by our team before being published. 
              You'll be notified once it's approved or if changes are needed.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BlogPostForm;
